import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fiveStarApi } from '../lib/api';
import { Star, Plus, ArrowRight, Calendar, CheckCircle2, Clock, X, Trash2, Archive, ArchiveRestore, ArrowUpDown } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import EmployeeTypeahead from '../components/EmployeeTypeahead';

const STEP_LABELS = {
  manager_rating: 'Awaiting Manager Rating',
  area_manager_review: 'Awaiting Area Manager Review',
  manager_synthesis: 'Awaiting Manager Synthesis',
  signed_off: 'Awaiting Sign-off',
  discussion_pending: 'Awaiting Employee Discussion',
  discussed: 'Discussed & Archived',
  expired: 'Expired (auto-closed after 60 days)',
};

// Workflow-status priority used by the "Pending first" sort — higher = more urgent.
const STATUS_PRIORITY = {
  manager_rating: 100,
  area_manager_review: 95,
  manager_synthesis: 90,
  signed_off: 60,
  discussion_pending: 55,
  discussed: 10,
  expired: 5,
};

const SORT_OPTIONS = [
  { key: 'pending_first', label: 'Pending first' },
  { key: 'newest',        label: 'Newest' },
  { key: 'oldest',        label: 'Oldest' },
  { key: 'name_asc',      label: 'Employee A → Z' },
  { key: 'cycle',         label: 'Cycle' },
];

const sortReviews = (list, key) => {
  const arr = [...list];
  const t = (r) => new Date(r.updated_at || r.created_at || 0).getTime();
  switch (key) {
    case 'newest':   arr.sort((a, b) => t(b) - t(a)); break;
    case 'oldest':   arr.sort((a, b) => t(a) - t(b)); break;
    case 'name_asc': arr.sort((a, b) => (a.employee_name || '').localeCompare(b.employee_name || '')); break;
    case 'cycle':    arr.sort((a, b) => (a.cycle_name || '').localeCompare(b.cycle_name || '') || t(b) - t(a)); break;
    case 'pending_first':
    default:
      arr.sort((a, b) => {
        const pa = STATUS_PRIORITY[a.status] ?? 0;
        const pb = STATUS_PRIORITY[b.status] ?? 0;
        if (pa !== pb) return pb - pa;
        return t(b) - t(a);
      });
      break;
  }
  return arr;
};

const stepBadge = (status) => {
  if (status === 'discussed') return 'bg-emerald-50 text-emerald-700';
  if (status === 'discussion_pending') return 'bg-amber-50 text-amber-700';
  return 'bg-fuchsia-50 text-fuchsia-700';
};

const REVIEW_TYPES = ['Introductory', 'Bi-Annual', 'AdHoc'];

const InitiateReviewModal = ({ open, onClose, onCreated }) => {
  const { adminRoles = [] } = useAuth();
  // Anyone with an admin/initiator role can self-attest as the Area Manager.
  // The backend re-checks the role too — this is just the UI gate.
  const userCanSelfAm = (adminRoles || []).some(r =>
    ['review_initiator', 'super_admin', 'template_admin', 'reports_admin'].includes(r)
  );
  const [employees, setEmployees] = useState([]);
  const [managers, setManagers] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [areaManagerId, setAreaManagerId] = useState('');
  const [iAmTheAM, setIAmTheAM] = useState(false);
  const [reviewType, setReviewType] = useState('Bi-Annual');
  const [cycleName, setCycleName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Item #6 — employees: all @therapymgmt.com, AM: review_initiator users only
    Promise.all([
      fiveStarApi.listEligibleEmployees().then(d => d?.employees || []).catch(() => []),
      fiveStarApi.listEligibleManagers().then(d => d?.managers || []).catch(() => []),
    ]).then(([emps, mgrs]) => {
      setEmployees(emps);
      setManagers(mgrs);
    });
    setEmployeeId('');
    setAreaManagerId('');
    setIAmTheAM(false);
    setReviewType('Bi-Annual');
    // Default cycle name e.g. "2026 H1 Bi-Annual"
    const now = new Date();
    const half = now.getMonth() < 6 ? 'H1' : 'H2';
    setCycleName(`${now.getFullYear()} ${half} Bi-Annual`);
  }, [open]);

  // Auto-suggest a sensible default name when type changes
  useEffect(() => {
    if (!open) return;
    const now = new Date();
    const year = now.getFullYear();
    if (reviewType === 'Bi-Annual') {
      const half = now.getMonth() < 6 ? 'H1' : 'H2';
      setCycleName(`${year} ${half} Bi-Annual`);
    } else if (reviewType === 'Introductory') {
      setCycleName('90-Day Introductory');
    } else if (reviewType === 'AdHoc') {
      setCycleName(`AdHoc ${now.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`);
    }
  }, [reviewType, open]);

  const handleEmployeeChange = (newEmpId) => {
    setEmployeeId(newEmpId);
    if (areaManagerId === newEmpId) setAreaManagerId('');
  };

  const areaManagerOptions = managers.filter(u => u.id !== employeeId);
  const selectedEmployee = employees.find(u => u.id === employeeId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!employeeId || !cycleName.trim()) {
      toast.error('Pick an employee and enter a name');
      return;
    }
    if (!iAmTheAM && !areaManagerId) {
      toast.error("An Area Manager is required — or check 'I am the Area Manager' if you are one");
      return;
    }
    setSubmitting(true);
    try {
      const created = await fiveStarApi.createReview({
        employee_user_id: employeeId,
        review_type: reviewType,
        cycle_name: cycleName.trim(),
        area_manager_user_id: iAmTheAM ? null : areaManagerId,
        manager_is_area_manager: iAmTheAM,
      });
      toast.success('Review initiated');
      onCreated(created);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to initiate review');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="initiate-review-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-slate-900">Initiate 5-Star Review</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" type="button"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              Employee to review <span className="text-rose-500">*</span>
            </label>
            <EmployeeTypeahead
              value={employeeId}
              onChange={handleEmployeeChange}
              options={employees}
              placeholder="Search by name or email…"
              testId="initiate-employee-select"
            />
            <div className="mt-1 text-[11px] text-slate-400">
              {employees.length > 0
                ? `${employees.length.toLocaleString()} active @therapymgmt.com users available.`
                : 'No active @therapymgmt.com users found.'}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              Area Manager <span className="text-rose-500">*</span>
            </label>
            {userCanSelfAm && (
              <label className="flex items-start gap-2 p-3 mb-2 rounded-md border border-cyan-200 bg-cyan-50/40 cursor-pointer hover:bg-cyan-50 transition" data-testid="initiate-i-am-am-row">
                <input
                  type="checkbox"
                  checked={iAmTheAM}
                  onChange={(e) => { setIAmTheAM(e.target.checked); if (e.target.checked) setAreaManagerId(''); }}
                  data-testid="initiate-i-am-am-checkbox"
                  className="mt-0.5 rounded border-slate-300 text-cyan-600 focus:ring-cyan-400"
                />
                <span className="text-xs text-slate-700">
                  <span className="font-semibold text-cyan-800">I am the Area Manager</span>
                  <span className="block text-[11px] text-slate-500 mt-0.5">
                    Skip the Area Manager review step. The workflow will go directly from Manager Rating to Synthesis.
                  </span>
                </span>
              </label>
            )}
            <EmployeeTypeahead
              value={areaManagerId}
              onChange={setAreaManagerId}
              options={areaManagerOptions}
              placeholder={iAmTheAM ? "Not needed — you are the Area Manager" : "Search area managers…"}
              testId="initiate-area-manager-select"
              disabled={!employeeId || iAmTheAM}
            />
            <div className="mt-1 text-[11px] text-slate-400">
              {!employeeId
                ? 'Pick an employee first, then assign an area manager.'
                : iAmTheAM
                  ? 'You will fulfill both roles — the Area Manager step will be skipped.'
                  : 'Required for every review. The area manager will review your input and can adjust ratings or add comments before synthesis.'}
            </div>
          </div>

          {/* Item #5 — Type dropdown */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                Type <span className="text-rose-500">*</span>
              </label>
              <select
                value={reviewType}
                onChange={(e) => setReviewType(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-md border border-slate-300 focus:outline-none focus:border-fuchsia-400 focus:ring-1 focus:ring-fuchsia-200 bg-white"
                data-testid="initiate-type-select"
                required
              >
                {REVIEW_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                Name <span className="text-rose-500">*</span>
              </label>
              <input
                value={cycleName}
                onChange={(e) => setCycleName(e.target.value)}
                placeholder="e.g. 2026 H1 Bi-Annual"
                className="w-full px-3 py-2 text-sm rounded-md border border-slate-300 focus:outline-none focus:border-fuchsia-400 focus:ring-1 focus:ring-fuchsia-200"
                data-testid="initiate-cycle-input"
                required
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold rounded-md border border-slate-200 hover:bg-slate-50">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !employeeId}
              data-testid="initiate-submit-btn"
              className="px-4 py-2 text-sm font-semibold rounded-md bg-gradient-to-r from-fuchsia-500 to-fuchsia-700 text-white shadow hover:shadow-lg disabled:opacity-60"
            >
              {submitting ? 'Creating…' : 'Initiate Review'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ReviewListItem = ({ review, viewerId, canDelete, onDelete, onArchiveChanged }) => {
  const isMine = review.employee_user_id === viewerId;
  const isAreaManagerHere = review.area_manager_user_id === viewerId;
  const [deleting, setDeleting] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const isArchivedForMe = (review.archived_by_users || []).includes(viewerId);
  const isCompleted = review.status === 'discussed' || review.status === 'signed_off';

  const ScoreBadge = () => (
    review.final_overall_score != null ? (
      <div className="flex items-center gap-1 text-amber-600 font-bold">
        <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
        {review.final_overall_score.toFixed(1)}
      </div>
    ) : (
      <Clock className="w-4 h-4 text-slate-400" />
    )
  );

  // Backend allows delete for super_admin OR the assigned Area Manager on this review.
  const canDeleteThis = canDelete || isAreaManagerHere;

  const handleDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`PERMANENTLY delete the ${review.cycle_name} review for ${review.employee_name}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await fiveStarApi.deleteReview(review.id);
      toast.success('Review deleted');
      onDelete?.(review.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleArchiveToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setArchiving(true);
    try {
      if (isArchivedForMe) {
        await fiveStarApi.unarchiveReview(review.id);
        toast.success('Moved back to your active list');
      } else {
        await fiveStarApi.archiveReview(review.id);
        toast.success('Archived — hidden from your active list');
      }
      onArchiveChanged?.(review.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Archive action failed');
    } finally {
      setArchiving(false);
    }
  };

  return (
    <div
      className="flex items-center gap-4 px-5 py-4 bg-white border border-slate-200 rounded-xl hover:border-slate-300 hover:shadow-sm transition mb-2"
      data-testid={`review-row-${review.id}`}
    >
      <Link
        to={`/five-star/review/${review.id}`}
        className="flex items-center gap-4 flex-1 min-w-0"
      >
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-fuchsia-500 text-white text-xs font-bold flex items-center justify-center">
          {(review.employee_name || review.employee_email || '?').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900 truncate">
            {isMine ? <>My review — <span className="text-slate-500 font-normal">by {review.manager_name}</span></> : review.employee_name}
          </div>
          <div className="text-xs text-slate-500 truncate">{review.cycle_name} · {STEP_LABELS[review.status] || review.status}</div>
        </div>
        <ScoreBadge />
        <span className={`hidden md:inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ${stepBadge(review.status)}`}>
          {review.status === 'discussed' ? 'Complete' : 'In Progress'}
        </span>
        <ArrowRight className="w-4 h-4 text-slate-400" />
      </Link>
      {isCompleted && (
        <button
          type="button"
          onClick={handleArchiveToggle}
          disabled={archiving}
          title={isArchivedForMe ? 'Restore to your active list' : 'Archive from your active list (only affects your view)'}
          data-testid={`${isArchivedForMe ? 'unarchive' : 'archive'}-review-${review.id}`}
          className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-600 border border-slate-200 hover:border-slate-600 rounded-md transition disabled:opacity-50"
        >
          {isArchivedForMe ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
        </button>
      )}
      {canDeleteThis && (
        <button
          type="button"
          onClick={handleDelete}
          disabled={deleting}
          title={canDelete ? 'Delete review (super-admin)' : 'Delete review (you are the Area Manager)'}
          data-testid={`delete-review-${review.id}`}
          className="p-1.5 text-rose-400 hover:text-white hover:bg-rose-600 border border-rose-200 hover:border-rose-600 rounded-md transition disabled:opacity-50"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

const FiveStarHome = () => {
  const { user, adminRoles = [] } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('managed');
  const [sortKey, setSortKey] = useState('pending_first');
  const [mine, setMine] = useState([]);
  const [managed, setManaged] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  // Item #6 — only review_initiators (or super_admin) can start a review
  const canInitiate = adminRoles.includes('review_initiator') || adminRoles.includes('super_admin');
  const canDelete = adminRoles.includes('super_admin');

  const handleDeletedLocally = (deletedId) => {
    setMine(prev => prev.filter(r => r.id !== deletedId));
    setManaged(prev => prev.filter(r => r.id !== deletedId));
  };

  const reload = async () => {
    setLoading(true);
    try {
      const [m, mg] = await Promise.all([
        fiveStarApi.listMyReviews(true).catch(() => []),
        fiveStarApi.listManagedReviews(true).catch(() => []),
      ]);
      setMine(m || []);
      setManaged(mg || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const handleCreated = (review) => {
    setModalOpen(false);
    reload();
    navigate(`/five-star/review/${review.id}`);
  };

  const handleArchiveChangedLocally = () => {
    // Refresh so the row hops to / from the Archive tab.
    reload();
  };

  const viewerId = user?.id;

  // Split each list into active (not personally archived) and archived buckets.
  const { activeManaged, activeMine, archived } = useMemo(() => {
    const isArch = (r) => (r.archived_by_users || []).includes(viewerId);
    const arch = [
      ...managed.filter(isArch),
      // Include mine-only archives that aren't already in managed
      ...mine.filter(r => isArch(r) && !managed.some(m => m.id === r.id)),
    ];
    return {
      activeManaged: managed.filter(r => !isArch(r)),
      activeMine: mine.filter(r => !isArch(r)),
      archived: arch,
    };
  }, [managed, mine, viewerId]);

  const rawVisible = tab === 'archive' ? archived : (tab === 'mine' ? activeMine : activeManaged);
  const visible = useMemo(() => sortReviews(rawVisible, sortKey), [rawVisible, sortKey]);

  return (
    <div className="px-12 pt-10 pb-20 max-w-[1400px]" data-testid="five-star-home">
      <Toaster position="top-right" richColors />
      <div className="flex items-end justify-between mb-7 flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[1px] text-slate-400 mb-1.5">5 Star Feedback &amp; Development</div>
          <h1 className="text-[28px] font-extrabold tracking-tight text-slate-900">Performance Reviews</h1>
          <p className="text-sm text-slate-500 mt-1">Multi-stage manager &amp; area-manager evaluations, synthesized into a clear development plan.</p>
        </div>
        {canInitiate ? (
          <button
            onClick={() => setModalOpen(true)}
            data-testid="initiate-review-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-fuchsia-500 to-fuchsia-700 text-white shadow hover:shadow-lg transition"
          >
            <Plus className="w-4 h-4" /> Initiate New Review
          </button>
        ) : (
          <div
            data-testid="initiate-review-disabled"
            className="text-xs text-slate-500 italic max-w-[280px] text-right"
            title="Only Review Initiators can start a review. Contact an admin to be granted this role."
          >
            Only Review Initiators can start a review.<br />
            Contact an admin to be added.
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
          <button
            onClick={() => setTab('managed')}
            data-testid="tab-managed"
            className={`px-4 py-1.5 text-sm font-semibold rounded-md transition ${tab === 'managed' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-800'}`}
          >
            As Manager / Area Manager ({activeManaged.length})
          </button>
          <button
            onClick={() => setTab('mine')}
            data-testid="tab-mine"
            className={`px-4 py-1.5 text-sm font-semibold rounded-md transition ${tab === 'mine' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-800'}`}
          >
            My Reviews ({activeMine.length})
          </button>
          <button
            onClick={() => setTab('archive')}
            data-testid="tab-archive"
            className={`px-4 py-1.5 text-sm font-semibold rounded-md transition inline-flex items-center gap-1.5 ${tab === 'archive' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-800'}`}
            title="Reviews you archived from your own view"
          >
            <Archive className="w-3.5 h-3.5" /> Archive ({archived.length})
          </button>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <label htmlFor="fs-sort" className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1.5">
            <ArrowUpDown className="w-3.5 h-3.5" /> Sort
          </label>
          <select
            id="fs-sort"
            data-testid="review-sort-select"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            className="text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:border-fuchsia-400 focus:ring-1 focus:ring-fuchsia-200"
          >
            {SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {loading && <div className="text-sm text-slate-500 py-6">Loading reviews…</div>}

      {!loading && visible.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl px-8 py-12 text-center" data-testid="review-empty-state">
          <Star className="w-8 h-8 text-slate-400 mx-auto mb-3" />
          <div className="text-sm text-slate-600 mb-1">
            {tab === 'archive' ? 'No archived reviews yet.'
              : tab === 'mine' ? 'No reviews yet for you.'
              : 'No active reviews.'}
          </div>
          <div className="text-xs text-slate-400">
            {tab === 'archive' ? 'Completed reviews you archive will appear here — click the archive icon on a completed row.'
              : tab === 'mine' ? "Your manager will initiate one when it's time."
              : 'Click "Initiate New Review" to start.'}
          </div>
        </div>
      )}

      {!loading && visible.length > 0 && (
        <div data-testid="review-list">
          {visible.map(r => (
            <ReviewListItem
              key={r.id}
              review={r}
              viewerId={user?.id}
              canDelete={canDelete}
              onDelete={handleDeletedLocally}
              onArchiveChanged={handleArchiveChangedLocally}
            />
          ))}
        </div>
      )}

      <InitiateReviewModal open={modalOpen} onClose={() => setModalOpen(false)} onCreated={handleCreated} />
    </div>
  );
};

export default FiveStarHome;
